import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiX } from 'react-icons/fi';
import Navbar from '../Customer/Navbar';

const LifeInsuranceProducts = () => {
  const navigate = useNavigate();  
  const [purchasedPolicies, setPurchasedPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const allProducts = [
    {
      id: 1,
      name: 'Max Life Smart Wealth Advantage Growth Par Plan',
      company: 'MAX LIFE',
      icon: '🛡️',
      description: 'A comprehensive life insurance plan with wealth accumulation benefits',
      features: ['Death Benefit', 'Maturity Benefit', 'Loan Facility', 'Flexible Premiums'],
      minPremium: '₹5,000',
      maxCoverage: '₹50,00,000',
      tenure: '10-30 years'
    },
    {
      id: 2,
      name: 'ICICI Pru GOLD',
      company: 'ICICI Prudential',
      icon: '🏆',
      description: 'Premium life insurance with guaranteed returns and bonuses',
      features: ['Guaranteed Returns', 'Annual Bonus', 'Accidental Benefit', 'Waiver of Premium'],
      minPremium: '₹10,000',
      maxCoverage: '₹1,00,00,000',
      tenure: '15-25 years'
    },
    {
      id: 3,
      name: 'Bajaj ACE PLAN',
      company: 'Bajaj Alliance',
      icon: '⭐',
      description: 'Flexible life insurance plan with multiple benefit options',
      features: ['Flexible Coverage', 'Rider Options', 'Tax Benefits', 'Easy Claims'],
      minPremium: '₹3,000',
      maxCoverage: '₹25,00,000',
      tenure: '5-40 years'
    },
    {
      id: 4,
      name: 'HDFC Life Click 2 Achieve',
      company: 'HDFC Life',
      icon: '🎯',
      description: 'Goal-based life insurance plan for your financial objectives',
      features: ['Goal Planning', 'Flexible Payouts', 'Loyalty Bonus', 'Online Management'],
      minPremium: '₹6,000',
      maxCoverage: '₹75,00,000',
      tenure: '10-35 years'
    }
  ];

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const customerData = sessionStorage.getItem('customerData');
      if (!customerData) {
        setError('Session expired. Please login again.');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      const customer = JSON.parse(customerData);
      const response = await fetch(`https://infrec.in/php-backend/backend/user/customerprofile.php?customer_id=${customer.id}`);
      const text = await response.text();
      const data = JSON.parse(text);

      if (data.success) {
        const lifeInsurancePolicies = data.purchases.filter(p => p.category_name === 'Life Insurance');
        setPurchasedPolicies(lifeInsurancePolicies);
      } else {
        setError(data.message || 'Failed to fetch policies');
      }
    } catch (err) {
      console.error('Error fetching policies:', err);
      setError('Error loading policies');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPolicy = async (product) => {
    try {
      const customerData = sessionStorage.getItem('customerData');
      if (!customerData) {
        setError('Session expired. Please login again.');
        return;
      }

      const customer = JSON.parse(customerData);
      const policyNumber = `LI-${Date.now()}`;

      const response = await fetch('https://infrec.in/php-backend/backend/user/addpolicy.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customer.id,
          policy_number: policyNumber,
          product_name: product.name,
          category_name: 'Life Insurance',
          subcategory_name: product.company,
          amount: 50000,
          status: 'Active',
          purchase_date: new Date().toISOString().split('T')[0],
          premium_frequency: 'Annual'
        })
      });

      const text = await response.text();
      const data = JSON.parse(text);

      if (data.success) {
        fetchPolicies();
      } else {
        setError(data.message || 'Failed to add policy');
      }
    } catch (err) {
      console.error('Error adding policy:', err);
      setError('Error adding policy');
    }
  };

  const handleRemovePolicy = async (policyId) => {
    try {
      const response = await fetch('https://infrec.in/php-backend/backend/user/deletepolicy.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ policy_id: policyId })
      });

      const text = await response.text();
      const data = JSON.parse(text);

      if (data.success) {
        fetchPolicies();
      } else {
        setError(data.message || 'Failed to remove policy');
      }
    } catch (err) {
      console.error('Error removing policy:', err);
      setError('Error removing policy');
    }
  };

  const isPolicyPurchased = (productName) => {
    return purchasedPolicies.some(p => p.product_name === productName);
  };

  const getPurchasedPolicy = (productName) => {
    return purchasedPolicies.find(p => p.product_name === productName);
  };

  const availableProducts = allProducts.filter(p => !isPolicyPurchased(p.name));

  const ProductCard = ({ product, isPurchased, policy }) => (
    <div
      className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4"
      style={{borderColor: '#305daa'}}
    >
      <div className="text-6xl mb-4">{product.icon}</div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h2>
      <p className="text-sm font-semibold mb-3" style={{color: '#305daa'}}>
        {product.company}
      </p>
      <p className="text-gray-600 mb-4">{product.description}</p>

      <div className="mb-4">
        <p className="text-sm font-semibold text-gray-700 mb-2">Key Features:</p>
        <div className="flex flex-wrap gap-2">
          {product.features.map((feature, idx) => (
            <span
              key={idx}
              className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-700"
            >
              {feature}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6 py-4 border-t border-b border-gray-200">
        <div>
          <p className="text-xs text-gray-600">Min Premium</p>
          <p className="font-semibold text-gray-900">{product.minPremium}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Max Coverage</p>
          <p className="font-semibold text-gray-900">{product.maxCoverage}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Tenure</p>
          <p className="font-semibold text-gray-900">{product.tenure}</p>
        </div>
      </div>

      {isPurchased && policy ? (
        <div className="space-y-3">
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-xs text-gray-600">Policy Number</p>
            <p className="font-semibold text-gray-900">{policy.policy_number}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-xs text-gray-600">Purchase Date</p>
              <p className="font-semibold text-gray-900">{new Date(policy.purchase_date).toLocaleDateString()}</p>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-xs text-gray-600">Amount</p>
              <p className="font-semibold" style={{color: '#305daa'}}>₹{parseFloat(policy.amount).toLocaleString('en-IN')}</p>
            </div>
          </div>
          <button
            onClick={() => handleRemovePolicy(policy.id)}
            className="w-full text-white py-2 rounded-lg hover:opacity-90 font-medium flex items-center justify-center gap-2"
            style={{backgroundColor: '#305daa'}}
          >
            <FiX /> Remove
          </button>
        </div>
      ) : (
        <button
          onClick={() => handleAddPolicy(product)}
          className="w-full text-white py-2 rounded-lg hover:opacity-90 font-medium flex items-center justify-center gap-2"
          style={{backgroundColor: '#305daa'}}
        >
          <FiPlus /> Add to Portfolio
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar customerName="Test Customer" />

      <div className="py-8 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 pt-12">
            <button
              onClick={() => navigate('/customer-profile')}
              className="flex items-center gap-2 text-white px-4 py-2 rounded-lg hover:opacity-90 mb-6"
              style={{backgroundColor: '#305daa'}}
            >
              <FiArrowLeft /> Back
            </button>

            <div className="text-white rounded-lg shadow-md p-8" style={{backgroundColor: '#305daa'}}>
              <h1 className="text-4xl font-bold mb-2">Life Insurance Products</h1>
              <p className="opacity-80">Explore our comprehensive life insurance plans</p>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading policies...</p>
            </div>
          ) : (
            <>
              {purchasedPolicies.length > 0 && (
                <div className="mb-12">
                  <div className="mb-6">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Your Purchased Products</h2>
                    <p className="text-gray-600">Products you have already purchased from Infrec</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {purchasedPolicies.map((policy) => {
                      const product = allProducts.find(p => p.name === policy.product_name);
                      return product ? (
                        <ProductCard key={policy.id} product={product} isPurchased={true} policy={policy} />
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {availableProducts.length > 0 && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Available Products</h2>
                    <p className="text-gray-600">Explore more life insurance products to add to your portfolio</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {availableProducts.map((product) => (
                      <ProductCard key={product.id} product={product} isPurchased={false} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LifeInsuranceProducts;
